import { Radio } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { PlatformRegionData } from 'services/api/dashboard';
import { useTranslate } from '@tolgee/react';
import { initEcharts, applyChartTheme, ECharts } from 'lib/echarts';
import styled from '@emotion/styled';
import { formatNumber } from 'utils/numberFormat';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { useGetRegionLabel } from 'hooks/business/useRegion';

function PlatformRegionChart({
  region,
  data,
  field,
}: {
  region: string;
  data: PlatformRegionData[];
  field: keyof Pick<
    PlatformRegionData,
    'activityTradeCount' | 'botTradeAmountList' | 'activityTradeAmountList'
  >;
}) {
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const [type, setType] = useState<'platform' | 'region'>('platform');

  const chartDomRef = useRef<HTMLDivElement>(null);

  const chartRef = useRef<ECharts>();

  useEffect(() => {
    if (!chartDomRef.current) {
      return;
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const chartData = data.reduce<{ value: number; name: string }[]>((res, cur) => {
      const { platform, region: curRegion, [field]: fieldValue } = cur;

      const value =
        typeof fieldValue === 'number'
          ? fieldValue
          : Number(fieldValue.find((_) => _.region === region)?.amount) || 0;

      const name = type === 'platform' ? platform : curRegion;

      const target = res.find((_) => _.name === name);

      if (target) {
        target.value += value;
        return res;
      }

      res.push({
        value,
        name,
      });

      return res;
    }, []);
    chartRef.current.setOption(
      applyChartTheme({
        color: ['#5B8FF9', '#5AD8A6', '#5D7092', '#F6BD16'],
        tooltip: {
          trigger: 'item',
        },
        series: [
          {
            type: 'pie',
            radius: ['52%', '60%'],
            data: chartData,
            label: {
              formatter: ({ name, value }: any) =>
                `${
                  type === 'platform'
                    ? (platformLabelMap as any)[toLowerCase(name) as any]
                    : getRegionLabel(name)
                }\n${formatNumber(value)}`,
              color: 'var(--fett-text-primary)',
              textBorderColor: 'transparent',
              textBorderWidth: 0,
            },
          },
        ],
      }),
    );
  }, [type, region, data, field, t, getRegionLabel]);

  return (
    <Container>
      <Radio.Group value={type} onChange={({ target: { value } }) => setType(value)}>
        <Radio.Button value="platform">{t('平台维度')}</Radio.Button>
        <Radio.Button value="region">{t('站点维度')}</Radio.Button>
      </Radio.Group>
      <section ref={chartDomRef} />
    </Container>
  );
}

const Container = styled.section`
  display: flex;
  flex-direction: column;
  > div {
    flex: 0 0 auto;
  }
  > section {
    flex: 1 0 0;
  }
`;

export default PlatformRegionChart;
